import dayjs from 'dayjs';

import type { IndividualPaymentParcelSaveViewModel } from '@/shared/api/generated';

import type { ParcelCreateFormValues } from '../hooks/useParcelCreateFormSchema';

/**
 * Monta o corpo do `POST` de criação de parcela.
 *
 * Três decisões estão materializadas aqui:
 *
 * 1. **`statusId` nunca é enviado.** É opcional no payload e não existe lookup de
 *    status de parcela; omitindo, quem define o status inicial é o backend — que é
 *    o dono da máquina de estados.
 * 2. **`multiple` e `amountOfParcel` só viajam juntos.** Com o interruptor
 *    desligado nenhum dos dois vai no corpo, para não enviar `multiple: false`
 *    acompanhado de uma quantidade órfã do preenchimento anterior.
 * 3. **A data sai como data-hora ISO.** O formulário guarda `YYYY-MM-DD` (o
 *    formato do `DatePicker`) e o schema gerado pede `date-time`, então a conversão
 *    fica nesta fronteira — como em `buildSaveViewModel` de class-module.
 *
 * Campos opcionais vazios são omitidos em vez de enviados como `null`: o corpo
 * carrega só o que o operador preencheu.
 *
 * @param values - Valores validados do formulário.
 * @returns Corpo do `POST`, pronto para o hook gerado.
 *
 * @example
 * ```ts
 * buildParcelCreatePayload({ typeId: 3, numberParcel: 13, total: 1180,
 *   paymentMethod: 1, expirationDate: '2026-09-10', multiple: false });
 * // { typeId: 3, numberParcel: 13, total: 1180, paymentMethod: 1,
 * //   expirationDate: '2026-09-10T03:00:00.000Z' }
 * ```
 */
export function buildParcelCreatePayload(
  values: ParcelCreateFormValues,
): IndividualPaymentParcelSaveViewModel {
  const expiration = dayjs(values.expirationDate);
  const isRecurrent = values.multiple === true;

  const optionalNumber = (
    key: keyof IndividualPaymentParcelSaveViewModel,
    value: number | null | undefined,
  ) => (value === null || value === undefined ? {} : { [key]: value });

  const optionalText = (
    key: keyof IndividualPaymentParcelSaveViewModel,
    value: string | null | undefined,
  ) =>
    value === null || value === undefined || value.trim() === '' ? {} : { [key]: value.trim() };

  // O schema gerado passa por `ToZod`, e o Zod infere alguns campos de forma opaca;
  // a forma real é a do `IndividualPaymentParcelSaveViewModel`.
  const text = (value: unknown): string | null | undefined =>
    typeof value === 'string' ? value : null;
  const amount = typeof values.amountOfParcel === 'number' ? values.amountOfParcel : null;

  return {
    typeId: values.typeId,
    numberParcel: values.numberParcel,
    total: values.total,
    paymentMethod: values.paymentMethod,
    ...(expiration.isValid() ? { expirationDate: expiration.toISOString() } : {}),
    ...optionalNumber('discountPunctualPercentage', values.discountPunctualPercentage),
    ...optionalNumber('retroactiveDiscount', values.retroactiveDiscount),
    ...optionalNumber('totalDiscount', values.totalDiscount),
    ...optionalNumber('fine', values.fine),
    ...optionalText('message', text(values.message)),
    ...optionalText('observation', text(values.observation)),
    ...(isRecurrent ? { multiple: true, ...optionalNumber('amountOfParcel', amount) } : {}),
  };
}
